import {useDispatch, useSelector} from "react-redux";
import React, {ButtonHTMLAttributes, ReactElement, lazy, useEffect, useState} from "react";
import {
    decrementModalCount,
    incrementModalCount,
    toggleHideBottomNav,
} from "@/redux/meta/metaActions";
import classNames from "classnames";
import {FaArrowLeft} from "react-icons/fa6";
import {LootBoxInstanceWithConfig, NftOwnership} from "@devour/client";
import {Button} from "@/components/buttons/Button";
import LazyWalletBoundary from "@/components/wallet/LazyWalletBoundary";
import useThemePreference from "@/hooks/useThemePreference";
import {useGetNftReward} from "@/hooks/useGetNftReward";
import {useGetRedeemableNftOwnerships} from "@/hooks/useGetRedeemableNftOwnerships";
import {IStore} from "@/redux/defaultStore";
import GoVipRewardsInventoryDrawerCard
    from "@/components/goVip/GoVipRewardsInventoryDrawerCard";
import Accordion from "@/components/Accordion";
import isLootboxUnburnt from "@/utils/isLootboxUnburnt";
import {useGetMyLootBoxInstances} from "@/hooks/useGetMyLootBoxInstances";
import VirtualLootBoxSelected from "@/components/goVip/VirtualLootBoxSelected";

/*
 * Lazy so these NFT-redeem components' thirdweb (parseChain / wallet status)
 * doesn't land in the GoVip drawer chunk — they load with the WalletProvider
 * boundary at their render sites below.
 */
const GoVipRewardsSelectedNft = lazy(() => import("@/components/goVip/GoVipRewardsSelectedNft"));
const GoVipRewardsSelectedXpMultiplier = lazy(() => import("@/components/goVip/GoVipRewardsSelectedXpMultiplier"));

interface Props {
    show: boolean;
    preselectedNftId?: string;
    toggle: () => void;
}

function GoVipRewardsInventoryDrawer(props: Props): ReactElement {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: redeemableNftsResponse, refetch: refetchRedeemableNftOwnerships} = useGetRedeemableNftOwnerships(fullToken);
    const redeemableNfts = redeemableNftsResponse?.lootboxes || [];
    const redeemableXpMultipliers = redeemableNftsResponse?.xpMultipliers || [];
    const xpMultiplierNftTrackerId = redeemableNftsResponse?.xpMultiplierNftTrackerId;
    const { isOnDarkMode, getThemedImageUrl } = useThemePreference();
    const {data: virtualInstancesResponse, refetch: refetchVirtualInstances} = useGetMyLootBoxInstances(fullToken);
    const virtualInstances: LootBoxInstanceWithConfig[] = virtualInstancesResponse?.lootBoxInstances || [];

    const [
        selectedNft,
        setSelectedNft,
    ] = useState<NftOwnership>(undefined);
    const [
        selectedVirtualInstance,
        setSelectedVirtualInstance,
    ] = useState<LootBoxInstanceWithConfig>(undefined);
    const {data: selectedReward} = useGetNftReward(selectedNft?.nftTrackerId);
    const showSelectedNft = selectedNft && selectedReward;
    /*
     * !selectedNft guard: virtual and on-chain selection are mutually exclusive;
     * selecting an NFT takes precedence and virtual view must not overlap
     */
    const showSelectedVirtual = !!selectedVirtualInstance && !selectedNft;


    useEffect(() => {
        if (props.show) {
            void refetchRedeemableNftOwnerships();
            void refetchVirtualInstances();
            dispatch(incrementModalCount()); // prevent the body from being scrollable when drawer is open
            if (props.preselectedNftId) {
                const findSelectedNft = redeemableNfts.find((nft) => nft.id === props.preselectedNftId);
                setSelectedNft(findSelectedNft);
            } else {
                setSelectedNft(undefined);
            }
            setSelectedVirtualInstance(undefined);
            toggleBottomNav(true);
        } else {
            toggleBottomNav(false);
        }

        return () => {
            toggleBottomNav(false);
            dispatch(decrementModalCount());
        };

    }, [props.show, props.preselectedNftId]);

    function toggleBottomNav(hide: boolean) {
        dispatch(toggleHideBottomNav(hide));
    }

    function handleGoBack(e: React.MouseEvent | React.TouchEvent): void {
        e.stopPropagation();

        if (selectedReward) {
            setSelectedNft(undefined);
            setSelectedVirtualInstance(undefined);
        } else if (selectedVirtualInstance) {
            setSelectedVirtualInstance(undefined);
        } else {
            props.toggle();
        }
    }

    function renderEmptyState(): ReactElement {
        return (
            <>
                <div className="govip-rewards-drawer_inventory_subheader">
                    Open loot boxes to claim prizes!
                </div>
                <div className="govip-rewards-drawer_inventory_empty">
                    <div className="govip-rewards-drawer_inventory_empty_question-mark">
                        <img
                            src={getThemedImageUrl(`${import.meta.env.VITE_CDN_URL}/images/govip-rewards-question-mark.svg`)}
                            alt="GoVIP rewards question mark"
                        />
                    </div>
                    <h3>Empty Inventory</h3>
                    <div className="govip-rewards-drawer_inventory_empty_message">
                        Oops! There are no loot box NFTs in your inventory.
                        Level up to 3 to nab a loot box NFT #1 provided by DevourPlay!
                    </div>
                    <Button variant={isOnDarkMode ? "primary" : "primary"}
                        onClick={props.toggle}
                    >
                        Start Earning XP
                    </Button>
                </div>
            </>
        );
    }

    function renderXpMultipliers() {
        if (!redeemableXpMultipliers?.length) {
            return null;
        }
        const inProgress = redeemableXpMultipliers.some((nftOwnership) => {
            return !isLootboxUnburnt(nftOwnership);
        });
        const header = <div className="d-flex gap-1 align-items-center">
            <div>
                XP Multipliers
            </div>
            <span className="govip-rewards-drawer_inventory_header_count">
                {redeemableXpMultipliers?.length}
            </span>
            {inProgress &&
                <Button variant="primary"
                    {...{disabled: true}}
                />
            }
        </div>;
        const body =
						<div className="govip-rewards-drawer_inventory_nfts">
						    {redeemableXpMultipliers.map(nft =>
						        <GoVipRewardsInventoryDrawerCard
						            key={nft.id}
						            nftOwnership={nft}
						            setSelectedNft={setSelectedNft}
						        />)}
						</div>;
        return (
            <Accordion
                header={header}
                body={body}
                showDivider={false}
                showBorder={false}
                showChevron={true}
                padding="1rem 0"
                isOpenDefault={redeemableNfts?.length === 0}
            />
        );
    }

    function renderLootboxes() {
        if (!redeemableNfts?.length) {
            return null;
        }
        const header = <div className="d-flex gap-1 align-items-center">
            <div>
                Loot Boxes
            </div>
            <span className="govip-rewards-drawer_inventory_header_count">
                {redeemableNfts?.length}
            </span>
        </div>;
        const body =
            <div className="govip-rewards-drawer_inventory_nfts">
                {redeemableNfts.map(nft =>
                    <GoVipRewardsInventoryDrawerCard
                        key={nft.id}
                        nftOwnership={nft}
                        setSelectedNft={setSelectedNft}
                    />)}
            </div>;
        return (
            <Accordion
                header={header}
                body={body}
                showDivider={false}
                showBorder={false}
                showChevron={true}
                isOpenDefault={true}
                padding="1rem 0"
            />
        );
    }

    function renderVirtualLootBoxes() {
        if (!virtualInstances?.length) {
            return null;
        }
        const header = <div className="d-flex gap-1 align-items-center">
            <div>
                Virtual Loot Boxes
            </div>
            <span className="govip-rewards-drawer_inventory_header_count">
                {virtualInstances.length}
            </span>
        </div>;
        const body =
            <div className="govip-rewards-drawer_inventory_nfts">
                {virtualInstances.map((item) =>
                    <button
                        key={item.instance.id}
                        className="govip-rewards-drawer_inventory_nfts_card"
                        onClick={() => {
                            setSelectedNft(undefined);
                            setSelectedVirtualInstance(item);
                        }}
                    >
                        <img
                            className="govip-rewards-drawer_inventory_nfts_card_image"
                            src={item.config?.image ?? `${import.meta.env.VITE_CDN_URL}/images/govip-nft-rewards-default-image.gif`}
                            alt={item.config?.name ?? "Loot Box"}
                        />
                    </button>)}
            </div>;
        return (
            <Accordion
                header={header}
                body={body}
                showDivider={false}
                showBorder={false}
                showChevron={true}
                isOpenDefault={true}
                padding="1rem 0"
            />
        );
    }

    return (
        <>
            <div
                className={classNames("govip-rewards-drawer-overlay", {
                    "is-active": props.show,
                })}
                onClick={props.toggle}
            />
            <div
                className={classNames("govip-rewards-drawer", {
                    "is-active": props.show,
                })}
            >
                <div className="govip-rewards-drawer_nav">
                    <div onClick={handleGoBack}>
                        <FaArrowLeft />
                        {selectedReward && "My Rewards"}
                    </div>
                </div>

                <div className={classNames(
                    "govip-rewards-drawer_reward-wrapper",
                    { "active": showSelectedNft || showSelectedVirtual },
                )}>
                    {showSelectedNft && selectedNft.nftTrackerId === xpMultiplierNftTrackerId &&
                        <LazyWalletBoundary>
                            <GoVipRewardsSelectedXpMultiplier
                                reward={selectedReward}
                                ownership={selectedNft}
                                toggleDrawer={props.toggle}
                                setSelectedNft={setSelectedNft}
                            />
                        </LazyWalletBoundary>
                    }
                    {showSelectedNft && selectedNft.nftTrackerId !== xpMultiplierNftTrackerId &&
                        <LazyWalletBoundary>
                            <GoVipRewardsSelectedNft
                                reward={selectedReward}
                                ownership={selectedNft}
                                toggleDrawer={props.toggle}
                                setSelectedNft={setSelectedNft}
                            />
                        </LazyWalletBoundary>
                    }
                    {showSelectedVirtual &&
                        <VirtualLootBoxSelected
                            item={selectedVirtualInstance}
                            toggleDrawer={props.toggle}
                            setSelectedVirtualInstance={setSelectedVirtualInstance}
                        />
                    }
                </div>
                {!showSelectedNft && !showSelectedVirtual &&
										<div className="govip-rewards-drawer_inventory">
										    <div className="govip-rewards-drawer_inventory_header">
										        <h3>My Rewards</h3>

										    </div>
										    {redeemableNfts?.length > 0 || redeemableXpMultipliers?.length > 0 || virtualInstances?.length > 0
										        ? <>
										            <div className="govip-rewards-drawer_inventory_subheader">
										                <p>
    Select a loot box to redeem or transfer!
										                </p>
										                <p>
    Only one multiplier can be active at a time.
    Activating a new one will replace your current multiplier.
										                </p>
										            </div>

										            {renderXpMultipliers()}
										            {renderLootboxes()}
										            {renderVirtualLootBoxes()}
										        </>
										        : renderEmptyState()}
										</div>
                }
            </div>
        </>
    );
}

export default GoVipRewardsInventoryDrawer;